<script setup lang="ts">
import type {FeedPost} from "~/repository/types";
import {useToast} from "~/components/ui/toast";
import {StaticModal} from "~/components/ui/modal";
import {IconBlock, IconDelete, IconPin, IconReport, IconUnpin} from "~/components/ui/icons";
import {ref} from "vue";

const props = defineProps<{
  post: FeedPost
}>();

const modalRef = ref<InstanceType<typeof StaticModal> | null>(null);

const {toast} = useToast();
const {t} = useI18n();
const {isLoggedIn} = useAuthStore();
const {sendEvent} = useEventLogger();

const emit = defineEmits<{
  'update:post': [payload: FeedPost],
  'handle:like': [payload: FeedPost],
  'delete:post': [payload: FeedPost],
  'remove:user-posts': [userId: number],
  'close-modal': []
}>();

const isWebShareSupported = ref(false);
// const repostablePost = ref<Post | null>(null);

const {pin, unpin, remove: removePost, repost} = usePostAPI(ref(props.post));

const {APP_URL} = useRuntimeConfig().public;


const copyLink = async () => {
  const postUrl = `${APP_URL}posts/${props.post.id}`;

  try {
    const response = await $larafetch("/api/v1/referral/links", {
      method: "POST",
      body: {
        return_url: postUrl,
      },
    });

    const referralCode = response?.code;
    if (!referralCode) throw new Error("Brak kodu referencyjnego");

    const finalUrl = `${postUrl}?ref=${referralCode}`;
    await navigator.clipboard.writeText(finalUrl);

    sendEvent("video_share", props.post.id);

    toast({
      title: t("toast.copy_link"),
      description: "Link referencyjny został skopiowany!",
      duration: 1500,
    });
  } catch (error) {
    console.error("Błąd przy generowaniu linku referencyjnego:", error);
    toast({
      title: "Błąd",
      description: "Nie udało się wygenerować linku referencyjnego.",
      duration: 1500,
      variant: "destructive",
    });
  }
};


const shareViaWebShareApi = () => {
  const url = `${APP_URL}posts/${props.post.id}`;

  if (!navigator.share) {
    console.error('Web Share API is not supported in this browser.');
    return;
  }

  navigator.share({
    title: document.title,
    text: 'Sprawdź ten link:',
    url: url,
  }).then(() => {
    sendEvent("video_share", props.post.id);
  }).catch((error) => console.error('Błąd udostępniania', error));
};

async function handlePin() {
  await pin();
  emit('update:post', {...props.post, is_pinned: true});
}

async function handleUnpin() {
  await unpin();
  emit('update:post', {...props.post, is_pinned: false});
}


onMounted(() => {
  isWebShareSupported.value = !!navigator.share;
});

const alertOpen = ref(false);
const dialogOpen = ref(false);

function handleOpenAlert() {
  alertOpen.value = true;
}

function handleOpenDialog() {
  dialogOpen.value = true;
}

function handleLike(event) {
  emit('handle:like', props.post);
  sendEvent(props.post.is_liked ? "post_dislike" : "post_like", props.post.id);
  event.currentTarget.classList.add('scale-up-animation');
  event.currentTarget.addEventListener('animationend', (event) => {
    event.currentTarget.classList.remove('scale-up-animation');
  });
}

const alertPostOpen = ref(false);

const handleOpenPostAlert = () => {
  alertPostOpen.value = true;
};


const confirmRemovePost = async () => {
  try {
    await removePost();
    emit("delete:post", props.post);
    toast({
      title: "Post został usunięty",
      duration: 1500,
    });
    alertPostOpen.value = false;
  } catch (err) {
    console.error("Błąd podczas usuwania posta:", err);
    toast({
      title: "Nie udało się usunąć posta",
      duration: 1500,
    });
  }
};

function handleRemovePost() {
  handleOpenPostAlert();
}

watch(alertOpen, (newVal) => {
  if (!newVal) {
    setTimeout(() => {
      document.body.removeAttribute('style');
    }, 200);
  }
});
watch(dialogOpen, (newVal) => {
  if (!newVal) {
    setTimeout(() => {
      document.body.removeAttribute('style');
    }, 200);
  }
});


// async function restrictUser(reason: 'view-restrict' | 'restrict' | 'block') {
//   const userId = props.post.author.id;
//   if (!userId) {
//     toast({title: t('toast.user_not_found'), variant: "destructive"});
//     return;
//   }
//
//   try {
//     const data = await $larafetch(`restrict/${userId}`, {
//       method: "POST",
//       body: {reason},
//     });
//     if (data.status) {
//       toast({title: t('toast.block_success'), duration: 1500});
//       alertOpen.value = false;
//     }
//   } catch (error) {
//     console.error(t('toast.error'), error);
//     toast({title: t('toast.error'), description: t('toast.restrict_error'), duration: 1500});
//   }
// }


async function restrictUser(reason: 'view-restrict' | 'restrict' | 'block') {
  const userId = props.post.author.id;
  if (!userId) {
    toast({title: t('toast.user_not_found'), variant: "destructive"});
    return;
  }

  try {
    const data = await $larafetch(`restrict/${userId}`, {
      method: "POST",
      body: {reason},
    });

    if (data.status) {
      toast({title: t('toast.block_success'), duration: 1500});
      alertOpen.value = false;
      emit("remove:user-posts", userId);
    }
  } catch (error: any) {
    if (error?.status === 403) {
      toast({
        title: t('toast.already_blocked'),
        description: t('toast.already_blocked_description'),
        duration: 2000,
      });
      alertOpen.value = false;
      return;
    }

    console.error(t('toast.error'), error);
    toast({
      title: t('toast.error'),
      description: t('toast.restrict_error'),
      duration: 1500,
      variant: "destructive"
    });
  }
}

// const copyEmbedCode = () => {
//   const embedCode = `<iframe src="${APP_URL}embed/${props.post.author.id}" width="400" height="700" style="border:none;" allowfullscreen></iframe>`;
//   navigator.clipboard.writeText(embedCode).then(() => {
//     alert("Kod osadzenia został skopiowany!");
//   });
// }
</script>
<template>
  <div class="flex flex-col items-center mb-1 shrink-0 grow-0 justify-start">
    <div class="flex flex-col items-center mb-5">
      <Button variant="action" class="px-0" @click="handleLike">
        <IconHeart :class="{'text-red-900 fill-red-900': post.is_liked}"/>
      </Button>
      <div v-if="post.likes_count > 0">
        <span class="text-white text-xs font-semibold">{{ post.likes_count }}</span>
      </div>
    </div>
    <div class="flex flex-col items-center mb-5">
      <StaticModal :height="75" ref="modalRef">
        <template #trigger="{ open }">
          <Button variant="action" class="px-0" @click="open()">
            <ClientOnly>
              <IconComment/>
            </ClientOnly>
          </Button>
          <div v-if="post.comments_count > 0">
            <span class="text-xs font-semibold text-white">{{ post.comments_count }}</span>
          </div>
        </template>
        <template #dialog-content>
          <NuxtLazyHydrate when-visible>
            <DialogContent class="max-w-xl">
              <DialogHeader>
                <button @click="modalRef?.toggleOpen()" class="w-auto text-left font-normal text-[17px]"
                        variant="ghost">
                  Anuluj
                </button>
                <h2 class="text-center">Comments</h2>
              </DialogHeader>
              <LazyModalsCommentsModal :post="post" @close="modalRef?.toggleOpen()"/>
            </DialogContent>
          </NuxtLazyHydrate>
        </template>
        <template #static-drawer-content>
          <NuxtLazyHydrate when-visible>
            <DrawerHeader>
              <h2 class="text-center">Comments</h2>
            </DrawerHeader>
            <LazyModalsCommentsModal :post="post" @close="modalRef?.toggleOpen()"/>
          </NuxtLazyHydrate>
        </template>
      </StaticModal>
    </div>
    <!--    <div class="flex flex-col items-center mb-5">-->
    <!--      <Button variant="action"-->
    <!--              class="px-0"-->
    <!--              :disabled="user?.username === post?.reposted_by?.username"-->
    <!--              @click="repost">-->
    <!--        <IconRepost v-if="user?.username !== post?.reposted_by?.username"/>-->
    <!--        <IconRepostComplete v-else color="#ffffff"/>-->
    <!--      </Button>-->
    <!--      <div v-if="post.reposts_count > 0">-->
    <!--        <span class="text-white text-xs font-semibold">{{ post.reposts_count }}</span>-->
    <!--      </div>-->
    <!--    </div>-->
    <div class="flex flex-col items-center mb-7">
      <DropdownDrawer :useShareIcon="true">
        <template #content-drawer>
          <DrawerDescription class="m-4 rounded-xl bg-neutral-100">
            <DrawerClose class="w-full">
              <DrawerMenuItem @click="copyLink">{{ $t('dropdown.copy_link') }}
                <ClientOnly>
                  <IconLink size="20px"/>
                </ClientOnly>
              </DrawerMenuItem>
            </DrawerClose>
            <DrawerMenuSeparator v-if="isWebShareSupported"/>
            <DrawerMenuItem v-if="isWebShareSupported" @click="shareViaWebShareApi" class="">{{
                $t('dropdown.share_link')
              }}
            </DrawerMenuItem>
          </DrawerDescription>
        </template>
        <template #content-dropdown>
          <DropdownMenuItem @click="copyLink" class="flex justify-between">{{ $t('dropdown.copy_link') }}
            <ClientOnly>
              <IconLink size="20px"/>
            </ClientOnly>
          </DropdownMenuItem>
        </template>
      </DropdownDrawer>
    </div>
    <DropdownDrawer :isPostIcon="true" v-if="isLoggedIn">
      <template #content-drawer>
        <DrawerDescription class="m-4 rounded-xl bg-neutral-100">
          <DrawerClose
            class="w-full select-none overflow-hidden text-primary font-semibold active:bg-gray-dark focus-visible:bg-gray-dark transform-colors first:rounded-t-xl last:rounded-b-xl flex justify-between items-center">
            <DrawerMenuItem v-if="!post.is_pinned && post.is_owner" @click="handlePin">
              {{ $t('dropdown.pin') }}
              <ClientOnly>
                <IconPin size="20px"/>
              </ClientOnly>
            </DrawerMenuItem>
          </DrawerClose>
          <DrawerClose
            class="w-full overflow-hidden select-none text-primary font-semibold active:bg-gray-dark focus-visible:bg-gray-dark transform-colors first:rounded-t-xl last:rounded-b-xl flex justify-between items-center">
            <DrawerMenuItem v-if="post.is_pinned && post.is_owner" @click="handleUnpin">
              {{ $t('dropdown.unpin') }}
              <ClientOnly>
                <IconUnpin size="20px"/>
              </ClientOnly>
            </DrawerMenuItem>
          </DrawerClose>
          <DrawerMenuSeparator v-if="post.is_owner"/>
          <DrawerClose
            class="w-full overflow-hidden select-none text-primary font-semibold active:bg-gray-dark focus-visible:bg-gray-dark transform-colors first:rounded-t-xl last:rounded-b-xl flex justify-between items-center">
            <DrawerMenuItem @click="handleOpenAlert" class="text-red">{{ $t('dropdown.block') }}
              <ClientOnly>
                <IconBlock size="20px"/>
              </ClientOnly>
            </DrawerMenuItem>
          </DrawerClose>
          <DrawerMenuSeparator/>
          <DrawerClose
            class="w-full overflow-hidden select-none text-primary font-semibold active:bg-gray-dark focus-visible:bg-gray-dark transform-colors first:rounded-t-xl last:rounded-b-xl flex justify-between items-center">
            <DrawerMenuItem @click="handleOpenDialog" class="text-red">{{ $t('dropdown.report') }}
              <ClientOnly>
                <IconReport size="20px"/>
              </ClientOnly>
            </DrawerMenuItem>
          </DrawerClose>
          <DrawerMenuSeparator v-if="post.is_owner"/>
          <DrawerClose
            class="w-full overflow-hidden select-none text-primary font-semibold active:bg-gray-dark focus-visible:bg-gray-dark overflow-hidden  transform-colors first:rounded-t-xl last:rounded-b-xl flex justify-between items-center">
            <DrawerMenuItem v-if="post.is_owner" @click="handleRemovePost"
                            class="text-red">
              {{ $t('dropdown.delete') }}
              <ClientOnly>
                <IconDelete size="20px"/>
              </ClientOnly>
            </DrawerMenuItem>
          </DrawerClose>
        </DrawerDescription>
      </template>
      <template #content-dropdown>
        <DropdownMenuItem class="flex justify-between" v-if="!post.is_pinned && post.is_owner" @click="handlePin">
          {{ $t('dropdown.pin') }}
          <ClientOnly>
            <IconPin size="20px"/>
          </ClientOnly>
        </DropdownMenuItem>
        <!--        <DropdownMenuItem @click="copyEmbedCode">Osadź</DropdownMenuItem>-->
        <DropdownMenuItem class="flex justify-between" v-if="post.is_pinned && post.is_owner" @click="handleUnpin">
          {{ $t('dropdown.unpin') }}
          <ClientOnly>
            <IconUnpin size="20px"/>
          </ClientOnly>
        </DropdownMenuItem>
        <DropdownMenuItem class="flex justify-between text-red" @click="handleOpenAlert">
          {{ $t('dropdown.block') }}
          <ClientOnly>
            <IconBlock size="20px"/>
          </ClientOnly>
        </DropdownMenuItem>
        <DropdownMenuItem class="flex justify-between text-red" @click="handleOpenDialog">
          {{ $t('dropdown.report') }}
          <ClientOnly>
            <IconReport color="#ff1856" size="20px"/>
          </ClientOnly>
        </DropdownMenuItem>
        <DropdownMenuItem v-if="post.is_owner" @click="handleRemovePost" class="text-red flex justify-between">
          {{ $t('dropdown.delete') }}
          <ClientOnly>
            <IconDelete size="20px"/>
          </ClientOnly>
        </DropdownMenuItem>
      </template>
    </DropdownDrawer>
    <Dialog v-model:open="dialogOpen">
      <DialogContent class="max-md:max-w-[90%]">
        <NuxtLazyHydrate when-visible>
          <LazyModalsReportModal @close="dialogOpen = false" :postId="post.id"/>
        </NuxtLazyHydrate>
      </DialogContent>
    </Dialog>
    <AlertDialog v-model:open="alertPostOpen">
      <NuxtLazyHydrate when-visible>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Czy na pewno chcesz usunąć ten post?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Po usunięciu, post nie będzie już widoczny i nie będzie możliwości jego przywrócenia.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anuluj</AlertDialogCancel>
            <AlertDialogAction @click="confirmRemovePost">Usuń</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </NuxtLazyHydrate>
    </AlertDialog>
    <AlertDialog v-model:open="alertOpen">
      <NuxtLazyHydrate when-visible>
        <AlertDialogContent class="p-0 gap-0 overflow-hidden">
          <AlertDialogHeader class="p-6">
            <AlertDialogTitle>
              {{ t('alert.block_user', {name: post?.author.username}) }}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {{ t('alert.block_description', {name: post?.author.username}) }}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter class="border-t-[0.5px] border-gray-400 h-16 grid grid-cols-2">
            <AlertDialogCancel class="bg-background h-full text-gray border-0 mt-0"> {{ t('buttons.Cancel') }}
            </AlertDialogCancel>
            <AlertDialogAction
              class="bg-background h-full border-l-[0.5px] rounded-none border-gray-400 text-red font-bold whitespace-normal"
              @click="restrictUser('block')">
              {{ t('dropdown.block') }}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </NuxtLazyHydrate>
    </AlertDialog>
  </div>
</template>
<style scoped>
@keyframes scaleUp {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.5);
  }
  100% {
    transform: scale(1);
  }
}

.scale-up-animation {
  animation: scaleUp 0.3s ease-in-out;
}
</style>